import { useEffect, useState } from 'react';

import { useQueries } from '@tanstack/react-query';

import { ENTITY_TYPES } from '@/shared/api/openmetadata/constants';
import { buildChildNodes } from '@/shared/lib/tree';
import type { AssetTreeNode } from '@/shared/lib/tree';

import { fetchIcebergDatabases, fetchIcebergSchemas } from '@/features/user-management/api/tablePermissionApi';

const ICEBERG_SERVICES = [
  { key: 'bronze-iceberg', name: 'bronze-iceberg' },
  { key: 'silver-iceberg', name: 'silver-iceberg' },
  { key: 'gold-iceberg', name: 'gold-iceberg' },
];

export const useIcebergTreeData = () => {
  const [fullTreeData, setFullTreeData] = useState<AssetTreeNode[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const databaseQueries = useQueries({
    queries: ICEBERG_SERVICES.map((service) => ({
      queryKey: ['iceberg-databases', service.name],
      queryFn: () => fetchIcebergDatabases({ serviceName: service.name }),
    })),
  });

  const allDatabasesLoaded = databaseQueries.every((q) => q.isSuccess);
  const isDatabasesLoading = databaseQueries.some((q) => q.isLoading);

  useEffect(() => {
    if (!allDatabasesLoaded) return;

    const loadSchemas = async () => {
      const treeNodes: AssetTreeNode[] = [];

      for (let i = 0; i < ICEBERG_SERVICES.length; i++) {
        const service = ICEBERG_SERVICES[i];
        const databaseResponse = databaseQueries[i].data;

        if (!databaseResponse?.hits?.hits) continue;

        const serviceNode: AssetTreeNode = {
          key: `service-${service.key}`,
          title: service.name,
          type: 'service',
          serviceType: 'iceberg',
          serviceName: service.name,
          isLeaf: false,
          children: [],
          data: {
            type: 'service',
            serviceType: 'iceberg',
            serviceName: service.name,
            parentEntityType: 'database',
          },
        };

        const expandingParams = {
          key: serviceNode.key,
          entityType: ENTITY_TYPES.DATABASE,
          index: 'database_search_index',
          useAggregation: false,
          serviceType: 'iceberg',
          serviceName: service.name,
        };

        const databaseNodes = buildChildNodes(databaseResponse, expandingParams);

        await Promise.all(
          databaseNodes.map(async (dbNode) => {
            const dbData = dbNode.data as {
              service?: { displayName?: string };
              displayName?: string;
              name?: string;
            };
            const serviceName = dbData.service?.displayName || '';
            const databaseName = dbData.displayName || dbData.name || '';

            try {
              const schemaResponse = await fetchIcebergSchemas({
                serviceName,
                databaseName,
              });

              if (schemaResponse.aggregations) {
                const expandingNodeParams = {
                  key: dbNode.key,
                  entityType: ENTITY_TYPES.SCHEMA,
                  index: 'dataAsset',
                  useAggregation: true,
                  serviceType: 'iceberg',
                  serviceName,
                  databaseName,
                };
                const schemaNodes = buildChildNodes(schemaResponse, expandingNodeParams);

                const schemaLeafNodes = schemaNodes.map((node) => ({
                  ...node,
                  isLeaf: true,
                  children: undefined,
                }));
                dbNode.children = schemaLeafNodes;
              }
            } catch (error) {
              console.error('Failed to load schemas for', databaseName, error);
            }
          })
        );

        serviceNode.children = databaseNodes;
        treeNodes.push(serviceNode);
      }

      setFullTreeData(treeNodes);
      setIsLoading(false);
    };

    loadSchemas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allDatabasesLoaded]);

  return {
    treeData: fullTreeData,
    isLoading: isDatabasesLoading || isLoading,
  };
};
